import clsx from 'clsx';
import React, { Suspense, lazy } from 'react';

import Button from '@components/Button';
import GiftWrapIcon from '@components/Images/Gift/GiftWrapIcon';
import IconArrow from '@components/Images/IconArrow';
import Spinner from '@components/Spinner';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { SelectedOffer } from '@utils/backend-api/subscribe';
import { FormLocation, LeadTypes } from '@utils/enums';

interface CheckoutProps {
  onBackClick: () => void;
  presentedOfferCodes: string[];
  selectedOffer: SelectedOffer;
  showGiftRecipientForm?: boolean;
}

const LazySubscriptionForm = lazy(() => import('@components/SubscriptionForm'));

const Checkout = ({
  onBackClick,
  presentedOfferCodes,
  selectedOffer,
  showGiftRecipientForm,
}: CheckoutProps) => {
  const hydrated = useServerSideRendering();
  const subheader = showGiftRecipientForm
    ? `Complete your gift subscription to ${selectedOffer.title}.`
    : `Your subscription to ${selectedOffer.title}`;

  return (
    <div className="px-16 pb-80 pt-56 sm:px-28 sm:pb-96 md:pt-72 lg:pb-112">
      <div className="m-auto max-w-full md:max-w-[466px]">
        {showGiftRecipientForm && (
          <GiftWrapIcon
            className={clsx('h-[24px] w-[67px]', {
              'text-pro-gold': selectedOffer.isPro,
              'text-primary-magenta': !selectedOffer.isPro,
            })}
          />
        )}

        <h2 className="m-0 font-suisse-works text-headline-xs text-black">Checkout</h2>
        <p className="mb-16 mt-8 text-body-lg font-light text-grayscale-charcoal">{subheader}</p>
        <Button
          as="button"
          onClick={onBackClick}
          className="mb-48 flex items-center gap-16 border-transparent bg-transparent sm:mb-32"
        >
          <IconArrow className="w-[17px] text-black" />
          <span className="font-suisse-intl text-button-sm text-black">Back</span>
        </Button>
        {hydrated && (
          <Suspense
            fallback={
              <div className="my-16 text-center">
                <Spinner />
              </div>
            }
          >
            <LazySubscriptionForm
              buttonText={showGiftRecipientForm ? 'Purchase gift subscription' : 'Subscribe'}
              formLocation={FormLocation.Subscribe}
              leadType={LeadTypes.SubscribeTestPage}
              presentedOfferCodes={presentedOfferCodes}
              selectedOffer={selectedOffer}
              showGiftRecipientForm={showGiftRecipientForm}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default Checkout;
